import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatInTimeZone } from 'date-fns-tz';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';
import { useMedia } from '@mlb/react-patterns/v4/utils/hooks/useMedia/useMedia';
import { useTranslate } from '../../utils/hooks/useTranslate';
import GDBREAKPOINTS from '../themes/breakpoints';
import TeamComponent from './components/TeamComponent';
import { getDateFormatOptions, getDateFormat, DATE_FORMATS } from '@gameday/utils/languageHelpers';
import {
    DetailsTeamWrapper,
    PreviewDetailsWrapper,
    PreviewInfoMain,
    PreviewInfoSub,
    PreviewInfoSubWrapper,
    PreviewInfoSubDivider,
    PreviewInfoWrapper,
    TeamWrapper
} from './PreviewInfo.style';

const DefaultTZ = 'America/New_York';

const { XS_MAX } = GDBREAKPOINTS;

const PreviewInfo = ({
    teams,
    venue,
    dateTime,
    isPreGameDelay,
    isCancelled,
    isPostponed,
    isSuspended,
    homeStandings,
    awayStandings,
    startTimeTBD = false,
    isLoading = false,
    language = 'en',
    localTimezone = DefaultTZ,
    deviceInteraction
}) => {
    const isMobile = useMedia({ maxWidth: XS_MAX });
    const translate = useTranslate();
    let statusKey = '';
    let fallback = '';
    if (isPreGameDelay) {
        statusKey = 'game_status_delayed';
        fallback = 'Delayed';
    } else if (isCancelled) {
        statusKey = 'game_status_cancelled';
        fallback = 'Cancelled';
    } else if (isPostponed) {
        statusKey = 'game_status_postponed';
        fallback = 'Postponed';
    } else if (isSuspended) {
        statusKey = 'game_status_suspended';
        fallback = 'Suspended';
    }

    const gameStatus = translate(statusKey, fallback);
    const TBDString = translate('label_tbd', 'TBD');

    // The main text info centered in the component
    const gameInfo = useMemo(() => {
        let formattedDate = '';
        let formattedTime = '';

        if (dateTime && localTimezone) {
            const options = getDateFormatOptions(language);
            const format = getDateFormat(language, DATE_FORMATS.LONG);
            formattedDate = formatInTimeZone(dateTime, localTimezone, format, options);
            formattedTime = startTimeTBD ? TBDString : formatInTimeZone(dateTime, localTimezone, 'h:mm a zz');
        }

        return (
            <PreviewDetailsWrapper>
                <PreviewInfoMain>
                    {
                        isLoading
                            ? <Skeleton width='100px' height='14px' margin='0px' />
                            : formattedDate
                    }
                </PreviewInfoMain>
                <PreviewInfoMain>
                    {
                        isLoading
                            ? <Skeleton width='65px' height='14px' margin='0px' />
                            : `${formattedTime}${gameStatus ? ` - ${gameStatus?.toUpperCase()}` : ''}`
                    }
                </PreviewInfoMain>

            </PreviewDetailsWrapper>
        );
    }, [gameStatus, dateTime, venue, isLoading, startTimeTBD, localTimezone]);

    const venueInfo = useMemo(() => {
        const {
            name,
            location: {
                city,
                stateAbbrev,
                country
            } = {}
        } = venue;
        return (
            venue
                ? (
                    <PreviewInfoSubWrapper>
                        <PreviewInfoSub isLoading={ isLoading }>
                            {
                                isLoading
                                    ? <Skeleton width='90px' height='14px' margin='0px' />
                                    : `${name}`
                            }
                        </PreviewInfoSub>
                        {isLoading ? null : <PreviewInfoSubDivider>•</PreviewInfoSubDivider> }
                        <PreviewInfoSub>
                            {
                                isLoading
                                    ? <Skeleton width='65px' height='14px' margin='0px' />
                                    : `${city}, ${stateAbbrev ?? country ?? ''}`
                            }
                        </PreviewInfoSub>
                    </PreviewInfoSubWrapper>
                )
                : null
        );
    }, [venue]);
    // The team components on the side
    const logoSize = isMobile ? '32' : '48';
    const homeTeam = isLoading
        ? <Skeleton width='120px' height='50px' margin='0' />
        : (
            <TeamComponent
                team={teams?.home}
                isAway
                standings={homeStandings}
                isMobile={isMobile}
                logoSize={logoSize}
                deviceInteraction={deviceInteraction}
            />
        );

    const awayTeam = isLoading
        ? <Skeleton width='120px' height='50px' margin='0' />
        : (
            <TeamComponent
                team={teams?.away}
                isAway={false}
                standings={awayStandings}
                isMobile={isMobile}
                logoSize={logoSize}
                deviceInteraction={deviceInteraction}
            />
        );

    return (
        <PreviewInfoWrapper>
            <DetailsTeamWrapper>
                <TeamWrapper logoSize={logoSize}>
                    { awayTeam }
                </TeamWrapper>
                { gameInfo }
                <TeamWrapper alignRight logoSize={logoSize}>
                    { homeTeam }
                </TeamWrapper>
            </DetailsTeamWrapper>
            {venueInfo}
        </PreviewInfoWrapper>
    );
};

PreviewInfo.propTypes = {
    teams: PropTypes.object,
    venue: PropTypes.object,
    dateTime: PropTypes.string,
    isPreGameDelay: PropTypes.bool,
    isCancelled: PropTypes.bool,
    isPostponed: PropTypes.bool,
    isSuspended: PropTypes.bool,
    ticketLink: PropTypes.string,
    homeStandings: PropTypes.object,
    awayStandings: PropTypes.object,
    startTimeTBD: PropTypes.bool,
    localTimezone: PropTypes.string,
    isLoading: PropTypes.bool,
    language: PropTypes.string,
    deviceInteraction: PropTypes.object
};

export default PreviewInfo;
